import { motion } from "framer-motion"
import { ArrowRight, Sparkles, BarChart, Target } from "lucide-react"
import { useAuth0 } from "@auth0/auth0-react"

export default function HeroSection() {
  const { loginWithRedirect } = useAuth0()

  const steps = [
    {
      number: "1",
      title: "AI-Driven Content Creation",
      description:
        "Submit your website URL, and our AI will handle keyword analysis, create engaging long-form content, and optimize SEO elements like images and meta data.",
      icon: Sparkles,
    },
    {
      number: "2",
      title: "Supercharge Your Content Strategy",
      description:
        "Enhance your site's performance with high-quality, AI-generated content designed to boost visibility and authority.",
      icon: BarChart,
    },
    {
      number: "3",
      title: "Maximize Organic Traffic",
      description:
        "Attract consistent, high-quality traffic and improve conversions with optimized content that ranks higher in search engines.",
      icon: Target,
    },
  ]

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  }

  return (
    <div
      id="how-it-works"
      className="relative bg-slate-900 overflow-hidden"
    > {/* Removed gradient */}
      {/* Background Grid (Kept for subtle effect) */}

      <div className="container relative mx-auto px-4 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >

            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
              Boost Your Rankings with{" "}
              <span className="bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent">
                AI-Optimized Content
              </span>
            </h1>

            <p className="text-slate-300 text-xl max-w-lg">
              Start driving more organic traffic to your site by creating quality content, ready in{" "}
              <span className="font-semibold text-orange-400">just a few days</span>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => loginWithRedirect()}
                className="px-6 py-3 text-white bg-gradient-to-r from-orange-500 to-rose-500 rounded-lg hover:opacity-90 transition-opacity flex items-center"
              >
                Generate Content That Ranks
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              {/* <button className="px-6 py-3 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 transition">
                Watch Demo
              </button> */}
            </div>

            {/* <div className="flex items-center gap-4 pt-4">
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-slate-900 bg-gradient-to-r from-orange-500 to-rose-500"
                  />
                ))}
              </div>
              <div className="text-sm text-slate-400">
                <span className="text-white font-semibold">500+</span> websites already ranking higher
              </div>
            </div> */}
          </motion.div>

          {/* Right Column */}
          <motion.div variants={container} initial="hidden" animate="show" className="relative">
            {/* Vertical line */}
            <div className="absolute left-[47px] top-[40px] bottom-[40px] w-[2px] bg-gradient-to-b from-orange-500/40 via-rose-500/40 to-transparent" />

            {/* Steps */}
            <div className="space-y-12">
              {steps.map((step, index) => (
                <motion.div key={index} variants={item} className="flex gap-8 group">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-2xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 flex items-center justify-center shadow-lg transform transition-transform group-hover:scale-110">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 flex items-center justify-center text-white font-bold text-xl">
                        {step.number}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <step.icon className="w-5 h-5 text-orange-400" />
                      <h3 className="text-white text-xl font-semibold">{step.title}</h3>
                    </div>
                    <p className="text-slate-300 leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
